import React, { useState } from "react";
import { ColDef, ColGroupDef } from "ag-grid-community";
import ReportPageShell from "../components/ReportPageShell";
import DetailTable from "../components/DetailTable";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { useDetailQuery } from "../shared/useDetailQuery";
import { useSearchGate } from "../shared/useSearchGate";
import {
  dateTimeCol,
  inclusionCol,
  intCol,
  moneyCol,
  textCol,
} from "../shared/detailColumns";

const LIMIT_OPTIONS = [500, 2000, 10000, 50000];
const DEFAULT_LIMIT = 2000;

const TOOLTIP =
  "Transaction detail (RP-2164) — one row per transaction on the settled sales in scope. Approved " +
  "transactions feed the Transactions count + amount; excluded rows are shaded with the reason. Scope by " +
  "event/date/vendor/location, then export. Totals cover the full set even when rows are capped.";

const CSV_LEGEND = [
  "",
  "One row per transaction on settled sales in the window/scope.",
  "Txn Count / Txn Amount (counted) = this row's contribution to the Transactions totals (Approved only).",
  "Real $? = Real (cash/card/cash-balance) vs Fake (promo-balance/token) funding.",
];

function transactionColumns(): (ColDef | ColGroupDef)[] {
  return [
    inclusionCol(),
    textCol("businessDay", "Business Day", { pinned: "left", width: 120 }),
    intCol("orderLegacyId", "Legacy Order #"),
    textCol("transactionId", "Txn Id", {
      width: 150,
      valueFormatter: (p) => (p.value as string) ?? "(synthetic)",
    }),
    textCol("eventName", "Event", { width: 160 }),
    textCol("vendorId", "Vendor Id", { width: 150 }),
    textCol("locationId", "Location Id", { width: 150 }),
    textCol("orderStatus", "Order Status", { width: 120 }),
    textCol("transactionStatus", "Txn Status", { width: 120 }),
    textCol("tender", "Tender", { width: 130, valueFormatter: (p) => (p.value as string) ?? "" }),
    {
      headerName: "Real $?",
      field: "isRealMoney",
      width: 90,
      valueGetter: (p) => ((p.data as { isRealMoney?: boolean } | undefined)?.isRealMoney ? "Real" : "Fake"),
      cellStyle: (p) => ({ color: p.value === "Real" ? "#15803d" : "#b45309" }),
    },
    moneyCol("transactionAmount", "Txn Amount"),
    intCol("transactionCountContribution", "Txn Count"),
    moneyCol("transactionAmountContribution", "Txn Amount (counted)"),
    dateTimeCol("transactionAtUtc", "Txn At (UTC)"),
    textCol("inclusionReason", "Inclusion Reason", { width: 280 }),
  ];
}

// Transaction detail page (RP-2164) — TransactionsList.
export default function TransactionReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const { submitted, isDirty, hasRun, search } = useSearchGate({ filters, limit });
  const state = useDetailQuery(
    submitted
      ? toQueryParams("TransactionsList", submitted.filters, { limit: submitted.limit })
      : null
  );

  return (
    <ReportPageShell
      title="Transactions"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
      filterVendor
      filterLocation
      actions={
        <label className="flex items-center gap-1 text-sm text-gray-600">
          Row limit
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
          >
            {LIMIT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n.toLocaleString()}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {(data) => (
        <DetailTable
          data={data}
          columnDefs={transactionColumns()}
          summaryMetrics={["TransactionCount", "TransactionAmount"]}
          emphasisMetric="TransactionAmount"
          exportName="Transactions"
          csvExtra={CSV_LEGEND}
        />
      )}
    </ReportPageShell>
  );
}
